import { fontAttrs } from "@/components/tools/printables/page-kit";
import type { PlacedText } from "@/lib/tool-kit/printables/reward-chart";
import type { HabitSheetLayout } from "@/lib/tool-kit/study-more/habit-sheet";

const INK = "#1a1a1a";
const RULE = "#6b6b6b";
const WEEKEND = "#ececec";
const BOLD = { kind: "sans", bold: true } as const;
const SANS = { kind: "sans" } as const;

function Label({ item, fill = INK }: { item: PlacedText; fill?: string }) {
  return (
    <text
      fill={fill}
      fontSize={item.size}
      textAnchor={item.anchor}
      x={item.x}
      y={item.y}
      {...fontAttrs(item.bold ? BOLD : SANS)}
    >
      {item.text}
    </text>
  );
}

/** A tick (check mark) centred on a cell. */
function tickPath(cx: number, cy: number, size: number): string {
  const half = size / 2;
  const round = (value: number) => Math.round(value * 100) / 100;
  return `M ${round(cx - half)} ${round(cy)} L ${round(cx - half * 0.3)} ${round(cy + half * 0.7)} L ${round(cx + half)} ${round(cy - half * 0.8)}`;
}

/** The habit sheet's drawing, in millimetres. */
export function HabitSheetArt({
  layout,
  accent,
}: {
  layout: HabitSheetLayout;
  accent: string;
}) {
  const [top, headerBottom, ...rest] = layout.hRules;
  const left = layout.vRules[0] ?? 0;
  const right = layout.vRules.at(-1) ?? 0;
  const bottom = layout.hRules.at(-1) ?? 0;
  return (
    <g>
      <Label fill={accent} item={layout.title} />
      <Label item={layout.month} />
      {layout.shaded.map((rect) => (
        <rect
          fill={WEEKEND}
          height={rect.height}
          key={rect.x}
          width={rect.width}
          x={rect.x}
          y={rect.y}
        />
      ))}
      {layout.vRules.map((x, index) => (
        <line
          key={x}
          stroke={RULE}
          strokeWidth={
            index === 0 || index === layout.vRules.length - 1 ? 0.4 : 0.2
          }
          x1={x}
          x2={x}
          y1={top}
          y2={bottom}
        />
      ))}
      {[top, headerBottom, ...rest].map((y, index) => (
        <line
          key={y}
          stroke={RULE}
          strokeWidth={index <= 1 ? 0.4 : 0.2}
          x1={left}
          x2={right}
          y1={y}
          y2={y}
        />
      ))}
      {layout.headings.map((item) => (
        <Label item={item} key={`${item.x}-${item.y}-${item.text}`} />
      ))}
      {layout.names.map((item) => (
        <Label item={item} key={`${item.y}-${item.text}`} />
      ))}
      {layout.totals.map((item) => (
        <Label item={item} key={item.y} />
      ))}
      {layout.ticks.map((tick) => (
        <path
          d={tickPath(tick.cx, tick.cy, layout.tickSize)}
          fill="none"
          key={`${tick.cx}-${tick.cy}`}
          stroke={accent}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={0.6}
        />
      ))}
    </g>
  );
}
